import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  Skeleton,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { ChatMessage } from './ChatMessage';
import {
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react';
import { useChatClient } from './useChatClient';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

// Skeleton for a single message
const MessageSkeleton = ({ isOwn }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      {!isOwn && (
        <Skeleton
          variant="circular"
          width={32}
          height={32}
          sx={{ mr: 1, bgcolor: colors.card, flexShrink: 0 }}
        />
      )}
      <Box className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        {!isOwn && (
          <Skeleton
            variant="text"
            width={80}
            height={14}
            sx={{ mb: 0.5, ml: 1, bgcolor: colors.card }}
          />
        )}
        <Skeleton
          variant="rounded"
          width={Math.random() * 100 + 150}
          height={40}
          sx={{
            bgcolor: isOwn ? `${colors.info}30` : colors.card,
            borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          }}
        />
      </Box>
    </Box>
  );
};

export const ChatMessageList = ({ messages, isLoading }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const {
    loadMoreMessages,
    hasMoreMessages,
    loadNewerMessages,
    hasMoreNewer,
    loadingDirection,
    pendingScrollToId,
    clearPendingScroll,
  } = useChatClient();
  const containerRef = useRef(null);
  const olderRef = useRef(null);
  const newerRef = useRef(null);
  const currentUserId = parseInt(cookies.get('userId'));
  const isInitialLoad = isLoading && messages.length === 0;

  // When newer messages are loaded, the code adjusts the scroll position so the user stays
  // where they were instead of the screen jumping or loading messages repeatedly.
  const awaitingNewerRef = useRef(false);
  const prevHeightRef = useRef(0);

  const handleLoadNewer = useCallback(() => {
    awaitingNewerRef.current = true;
    loadNewerMessages();
  }, [loadNewerMessages]);

  // No dep array: this has to compare against the height at the previous render
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const added = root.scrollHeight - prevHeightRef.current;
    prevHeightRef.current = root.scrollHeight;

    // Only the downwards direction needs this. Older messages arrive at the far end from
    // the scroll anchor, so they never move the view — which is why scrolling up is smooth.
    if (awaitingNewerRef.current && added > 0) {
      root.scrollTop -= added;
      awaitingNewerRef.current = false;
    }
  });

  // Nothing left to load downwards — drop a correction we never got to apply
  useEffect(() => {
    if (!hasMoreNewer || messages.length === 0)
      awaitingNewerRef.current = false;
  }, [hasMoreNewer, messages.length]);

  // Keep the latest loading functions in refs so the observer stays stable
  //  and doesn't accidentally trigger multiple times.
  const loadOlderRef = useRef(loadMoreMessages);
  const loadNewerRef = useRef(handleLoadNewer);
  useEffect(() => {
    loadOlderRef.current = loadMoreMessages;
    loadNewerRef.current = handleLoadNewer;
  }, [loadMoreMessages, handleLoadNewer]);

  //  Watch both ends of the chat, and when the user gets close to either end, automatically load
  // more messages before they reach the end.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const watch = (node, onReach) => {
      if (!node) return null;
      const observer = new IntersectionObserver(
        ([entry]) => entry.isIntersecting && onReach(),
        // Start loading a screen early so the user rarely sees the spinner
        { root, rootMargin: '400px' },
      );
      observer.observe(node);
      return observer;
    };

    const observers = [
      hasMoreMessages
        ? watch(olderRef.current, () => loadOlderRef.current())
        : null,
      hasMoreNewer
        ? watch(newerRef.current, () => loadNewerRef.current())
        : null,
    ];
    return () => observers.forEach((o) => o?.disconnect());
    // messages.length: the markers only exist once the list has rendered, and a new page
    // means we should re-check whether the end is still in view
  }, [hasMoreMessages, hasMoreNewer, messages.length]);

  // After jumping from a search result, bring that message into view and flash it
  useEffect(() => {
    if (!pendingScrollToId || !containerRef.current) return;

    const row = containerRef.current.querySelector(
      `[data-message-id="${pendingScrollToId}"]`,
    );
    if (!row) return;

    row.scrollIntoView({ block: 'center' });
    // Done directly on the node so this doesn't re-render every message
    row.classList.add('chat-message-flash');
    const timer = setTimeout(
      () => row.classList.remove('chat-message-flash'),
      2000,
    );
    clearPendingScroll();
    return () => clearTimeout(timer);
  }, [pendingScrollToId, messages, clearPendingScroll]);

  const getTimeKey = (dateString) => {
    try {
      const date = new Date(dateString);
      return `${date.getHours()}:${date.getMinutes()}`;
    } catch {
      return '';
    }
  };

  const groupedMessages = useMemo(() => {
    const groups = {};
    messages.forEach((message) => {
      const date = new Date(message.createdAt).toLocaleDateString('de-DE', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  }, [messages]);

  // Show skeleton during initial load
  if (isInitialLoad) {
    return (
      <Box
        className="flex flex-col-reverse h-full overflow-hidden px-4 py-2 gap-3"
        sx={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.bgInput,
            borderRadius: '2px',
          },
        }}
      >
        {/* Skeleton messages */}
        <MessageSkeleton isOwn={true} />
        <MessageSkeleton isOwn={false} />
        <MessageSkeleton isOwn={false} />
        <MessageSkeleton isOwn={true} />
        <MessageSkeleton isOwn={false} />
        <MessageSkeleton isOwn={true} />
        <MessageSkeleton isOwn={false} />
        <MessageSkeleton isOwn={true} />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className="flex flex-col-reverse h-full overflow-y-auto px-4 py-2 gap-1"
      sx={{
        // Keep overscroll inside the list so reaching the top/bottom doesn't chain
        // into an iOS visual-viewport pan of the page behind the chat.
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        // We correct the scroll position ourselves when older/newer pages come in, so
        // don't let the browser also try to compensate — the two fight each other.
        overflowAnchor: 'none',
        // Brief highlight on the message you jumped to from search
        '@keyframes chatMessageFlash': {
          '0%, 60%': { backgroundColor: `${colors.info}33` },
          '100%': { backgroundColor: 'transparent' },
        },
        '& .chat-message-flash': {
          animation: 'chatMessageFlash 2s ease-out',
          borderRadius: '12px',
        },
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: colors.bgInput,
          borderRadius: '2px',
        },
      }}
    >
      {/* First child = bottom of the list, because the container is column-reverse.
          This marker pulls in newer messages when reading history. */}
      {hasMoreNewer && messages.length > 0 && (
        <Box
          ref={newerRef}
          sx={{ height: 1, flexShrink: 0 }}
          aria-hidden="true"
        />
      )}

      {Object.entries(groupedMessages).map(([date, dateMessages]) => (
        <Box key={date} className="flex flex-col-reverse gap-1">
          {dateMessages.map((message, index) => {
            const isOwn = message.sender?.id === currentUserId;
            const prevMessage = dateMessages[index - 1];
            const showAvatar =
              !isOwn &&
              (index === 0 || prevMessage?.sender?.id !== message.sender?.id);

            // Show time only if this is the first message in a group
            // (different sender or different minute than the previous message)
            const showTime =
              index === 0 ||
              prevMessage?.sender?.id !== message.sender?.id ||
              getTimeKey(prevMessage?.createdAt) !==
                getTimeKey(message.createdAt);

            return (
              <ChatMessage
                key={message.id}
                message={message}
                isOwn={isOwn}
                showAvatar={showAvatar}
                showTime={showTime}
              />
            );
          })}

          <Box className="flex items-center justify-center my-3">
            <Box
              className="px-3 py-1 rounded-full"
              sx={{
                backgroundColor: colors.bgInput,
                color: colors.text,
                opacity: 0.6,
                fontSize: '0.7rem',
              }}
            >
              {date}
            </Box>
          </Box>
        </Box>
      ))}

      {/* Spinner for the upwards direction — renders at the top of the list */}
      {loadingDirection === 'older' && messages.length > 0 && (
        <Box className="flex justify-center py-4">
          <CircularProgress size={20} sx={{ color: colors.info }} />
        </Box>
      )}

      {/* Last child = top of the list. Pulls in older messages. */}
      {hasMoreMessages && messages.length > 0 && (
        <Box
          ref={olderRef}
          sx={{ height: 1, flexShrink: 0 }}
          aria-hidden="true"
        />
      )}

      {!isLoading && messages.length === 0 && (
        <Box className="flex items-center justify-center h-full">
          <Typography
            variant="body2"
            sx={{ color: colors.text, opacity: 0.4, textAlign: 'center' }}
          >
            {t('No messages yet.')}
            <br />
            {t('Start the conversation!')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
